import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Map, List } from 'immutable';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Table, TableContainer, TableHead, TableRow, TableCell, TableBody, TextField, Divider, Paper, IconButton, MenuItem, InputAdornment } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import Button from 'src/component/UI/Button';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';
import 'src/screen/Protected/DeviceScreen/TestingReport/TestingReport.scss';

// Form-oriented table for commissioning/audit extras, with cell-level onChange handlers and
// dynamic rows. Differs from DataTable, which displays static report data.
export default function CircuitDynamicTable (props) {
  const { title, columns, data, extrasKey, onChange, readOnly, typeLegend, deletable, minRows, numbered, addable } = props;
  const { t } = useTranslation();
  const isMobile = useMediaQuery(`(max-width: ${variables.phoneWidth})`);

  const rows = data || List();
  const canDelete = deletable && !readOnly && rows.size > minRows;

  const emptyRow = () => {
    const row = {};
    columns.forEach(col => { row[col.key] = ''; });
    return Map(row);
  };

  // extrasKey is a key under `extras`, or an array for a nested path e.g. ['inspection','inspectors'].
  const extrasPath = ['extras', ...(Array.isArray(extrasKey) ? extrasKey : [extrasKey])];

  const addRow = () => {
    onChange(extrasPath, rows.push(emptyRow()));
  };

  const deleteRow = (rowIndex) => {
    onChange(extrasPath, rows.delete(rowIndex));
  };

  const onCellChange = (rowIndex, colKey, val) => {
    onChange(extrasPath, rows.setIn([rowIndex, colKey], val));
  };

  const cellInput = (col, rowData, rowIndex) => {
    const lockedLabel = !readOnly && (col.locked || (rowIndex === 0 && col.lockFirstRow));
    const disabled = readOnly || col.locked || (rowIndex === 0 && col.lockFirstRow);
    const value = rowData.get(col.key) || '';
    const onCell = (e) => onCellChange(rowIndex, col.key, e.target.value);
    // Fixed by the form rather than unavailable, so it keeps normal text colour.
    const className = lockedLabel ? 'locked-cell' : undefined;

    // A column with `options` is an exclusive choice, e.g. one deficiency code per row.
    // `type` and the date label shrink don't apply to a select, so they're kept apart.
    if (col.options) {
      return (
        <TextField select fullWidth className={className} disabled={disabled} value={value} onChange={onCell}>
          {col.options.map(option => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
      );
    }

    return (
      <TextField
        fullWidth
        className={className}
        disabled={disabled}
        type={col.type === 'date' || col.type === 'number' ? col.type : 'text'}
        InputLabelProps={col.type === 'date' ? { shrink: true } : undefined}
        InputProps={col.unit ? { endAdornment: <InputAdornment position='end'>{col.unit}</InputAdornment> } : undefined}
        value={value}
        onChange={onCell}
      />
    );
  };

  const desktopTable = () => (
    <TableContainer>
      <Table stickyHeader>
        <colgroup>
          {numbered && <col className='row-number-column' />}
          {columns.map(col => (
            <col key={col.key} style={{ width: col.width || 'auto' }} />
          ))}
          {deletable && <col className='delete-column' />}
        </colgroup>
        <TableHead>
          <TableRow>
            {numbered && <TableCell className='main-headers'>{t('commissioningCircuit.common.number')}</TableCell>}
            {columns.map(col => (
              <TableCell key={col.key} className='main-headers'>{col.label}</TableCell>
            ))}
            {deletable && <TableCell className='main-headers' />}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((rowData, rowIndex) => (
            <TableRow key={rowIndex}>
              {numbered && <TableCell className='row-number-cell'>{rowIndex + 1}</TableCell>}
              {columns.map(col => (
                <TableCell key={col.key} className='body-cell'>
                  {cellInput(col, rowData, rowIndex)}
                </TableCell>
              ))}
              {deletable && (
                <TableCell className='body-cell'>
                  <IconButton
                    aria-label={t('common.delete')}
                    disabled={!canDelete}
                    onClick={() => deleteRow(rowIndex)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          )).toArray()}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const mobileCards = () => (
    <>
      {rows.map((rowData, rowIndex) => (
        <React.Fragment key={rowIndex}>
          <TableContainer className='mobile-junction-table'>
            <TableBody>
              {numbered && (
                <TableRow>
                  <TableCell className='sub-headers min-sub-headers center-items small-border remove-padding-for-junction-table2'>
                    <div>{t('commissioningCircuit.common.number')}</div>
                  </TableCell>
                  <TableCell className='body-cell'>{rowIndex + 1}</TableCell>
                </TableRow>
              )}
              {columns.map((col, idx) => (
                <TableRow key={`${col.key}-${idx}`}>
                  <TableCell className='sub-headers min-sub-headers center-items small-border remove-padding-for-junction-table2'>
                    <div>{col.label}</div>
                  </TableCell>
                  <TableCell className='body-cell'>
                    {cellInput(col, rowData, rowIndex)}
                  </TableCell>
                </TableRow>
              ))}
              {deletable && (
                <TableRow>
                  <TableCell className='sub-headers min-sub-headers center-items small-border remove-padding-for-junction-table2' />
                  <TableCell className='body-cell'>
                    <IconButton
                      aria-label={t('common.delete')}
                      disabled={!canDelete}
                      onClick={() => deleteRow(rowIndex)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </TableContainer>
          <Divider className='dynamic-table-divider' />
        </React.Fragment>
      )).toArray()}
    </>
  );

  return (
    <Paper className='dynamic-table-paper'>
      <h3>{title}</h3>
      {typeLegend && <p><i>{typeLegend}</i></p>}
      {isMobile ? mobileCards() : desktopTable()}
      {addable && (
        <div className='button-bar at-Junction'>
          <Button onClick={addRow} disabled={readOnly} border background backgroundColor={'white'} className='add-row'>
            {t('commissioningCircuit.common.addRow')}
          </Button>
        </div>
      )}
    </Paper>
  );
}

CircuitDynamicTable.propTypes = {
  title: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    // 'date' and 'number' map to the matching input type; anything else renders as text.
    type: PropTypes.string,
    width: PropTypes.string,
    // Unit suffix rendered inside the input, e.g. 'EA'. Matches CountVerdictTable.
    unit: PropTypes.string,
    // Disables this column's input on row 0, for tables whose first row is a fixed label.
    lockFirstRow: PropTypes.bool,
    // Disables this column on every row, for a label column the form fixes rather than the
    // operator entering. Pair with addable={false} so the rows stay as seeded.
    locked: PropTypes.bool,
    // Renders the cell as a select of these choices instead of a free-text input.
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })),
  })).isRequired,
  data: ImmutablePropTypes.list,
  extrasKey: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  typeLegend: PropTypes.string,
  deletable: PropTypes.bool,
  minRows: PropTypes.number,
  // Leading 1-based row counter, for forms whose paper original numbers its rows.
  numbered: PropTypes.bool,
  // Hide the Add Row button, for tables whose rows are fixed by the paper form.
  addable: PropTypes.bool,
};

CircuitDynamicTable.defaultProps = {
  data: List(),
  readOnly: false,
  deletable: false,
  minRows: 0,
  numbered: false,
  addable: true,
};
